import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAvailableStates, useScopeCategories } from '@/hooks/api/usePermits';
import type { PermitCalculationRequest } from '@/types/api.types';
import { CityCombobox } from '@/components/ui/CityCombobox';
import { MN_CITIES } from '@/data/mn_cities';

// Form validation schema
const permitSearchSchema = z.object({
  city: z.string().min(1, 'City is required').max(100, 'City name too long'),
  state: z.string().regex(/^[A-Z]{2}$/, 'State must be a 2-letter code (e.g., MN, CA)'),
  scope_category: z.string().min(1, 'Project type is required'),
  circuit_count: z
    .number()
    .int()
    .min(1, 'At least 1 circuit required')
    .max(50, 'Maximum 50 circuits'),
  inspection_count: z
    .number()
    .int()
    .min(1, 'At least 1 inspection required')
    .max(5, 'Maximum 5 inspections'),
  occupancy_type: z.enum(['residential', 'commercial'], {
    message: 'Select residential or commercial',
  }),
  job_description: z.string().max(4000, 'Description too long (max 4000 characters)').optional(),
  project_value: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(0, 'Project value must be positive').optional()
  ),
});

type PermitSearchFormData = z.infer<typeof permitSearchSchema>;

interface PermitSearchFormProps {
  onSubmit: (data: PermitCalculationRequest) => void;
  isLoading?: boolean;
  initialValues?: Partial<PermitCalculationRequest>;
}

// Currency formatting helper
const formatCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : value;
  if (isNaN(num) || num === 0) return '';
  return `$${num.toFixed(2)}`;
};

export default function PermitSearchForm({
  onSubmit,
  isLoading = false,
  initialValues,
}: PermitSearchFormProps) {
  const [selectedState, setSelectedState] = useState<string>('');
  const [projectValueDisplay, setProjectValueDisplay] = useState<string>(
    initialValues?.project_value ? formatCurrency(initialValues.project_value) : ''
  );

  // Fetch available options
  const { data: statesData, isLoading: statesLoading } = useAvailableStates();
  const { data: scopeData, isLoading: scopeLoading } = useScopeCategories();

  // Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<PermitSearchFormData>({
    resolver: zodResolver(permitSearchSchema) as Resolver<PermitSearchFormData>,
    defaultValues: {
      city: initialValues?.city || '',
      state: initialValues?.state || '',
      scope_category: initialValues?.scope_category || '',
      circuit_count: initialValues?.circuit_count || 1,
      inspection_count: initialValues?.inspection_count || 1,
      occupancy_type: (initialValues?.occupancy_type as 'residential' | 'commercial') || 'residential',
      job_description: initialValues?.job_description || '',
      project_value: initialValues?.project_value,
    },
  });

  const watchedState = watch('state');

  // Update selected state when form value changes
  useEffect(() => {
    if (watchedState && watchedState.length === 2) {
      setSelectedState(watchedState.toUpperCase());
    }
  }, [watchedState]);

  // Handle project value input with currency formatting
  const handleProjectValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    setProjectValueDisplay(rawValue ? `$${rawValue}` : '');
    const numValue = parseFloat(rawValue);
    if (!isNaN(numValue)) {
      setValue('project_value', numValue);
    } else {
      setValue('project_value', undefined as unknown as number);
    }
  };

  const handleProjectValueBlur = () => {
    const value = watch('project_value');
    if (value) {
      setProjectValueDisplay(formatCurrency(value));
    }
  };

  const handleFormSubmit = (data: PermitSearchFormData) => {
    const submitData: PermitCalculationRequest = {
      city: data.city,
      state: data.state.toUpperCase(),
      scope_category: data.scope_category,
      circuit_count: data.circuit_count,
      inspection_count: data.inspection_count,
      occupancy_type: data.occupancy_type,
      job_description: data.job_description || undefined,
      project_value: data.project_value,
    };
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Location Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* State */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              id="state"
              {...register('state')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={statesLoading || isLoading}
            >
              <option value="">Select State...</option>
              {statesData?.states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <CityCombobox
                  id="city"
                  value={field.value}
                  onChange={field.onChange}
                  cities={MN_CITIES}
                  placeholder={!selectedState ? 'Select state first...' : 'Type to search cities...'}
                  disabled={!selectedState || isLoading}
                  error={errors.city?.message}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Project Details Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
        <div className="space-y-4">
          {/* Scope Category */}
          <div>
            <label
              htmlFor="scope_category"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Project Type <span className="text-red-500">*</span>
            </label>
            <select
              id="scope_category"
              {...register('scope_category')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={scopeLoading || isLoading}
            >
              <option value="">Select Project Type...</option>
              {scopeData && (
                <>
                  <optgroup label="Residential">
                    {scopeData.residential.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} - {cat.description}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Commercial">
                    {scopeData.commercial.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} - {cat.description}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Other">
                    {scopeData.other.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} - {cat.description}
                      </option>
                    ))}
                  </optgroup>
                </>
              )}
            </select>
            {errors.scope_category && (
              <p className="mt-1 text-sm text-red-600">{errors.scope_category.message}</p>
            )}
          </div>

          {/* Occupancy Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Occupancy Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="residential"
                  {...register('occupancy_type')}
                  className="mr-2"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-700">Residential</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="commercial"
                  {...register('occupancy_type')}
                  className="mr-2"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-700">Commercial</span>
              </label>
            </div>
            {errors.occupancy_type && (
              <p className="mt-1 text-sm text-red-600">{errors.occupancy_type.message}</p>
            )}
          </div>

          {/* Circuit Count */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="circuit_count"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Number of Circuits <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="circuit_count"
                {...register('circuit_count', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                min={1}
                max={50}
              />
              {errors.circuit_count && (
                <p className="mt-1 text-sm text-red-600">{errors.circuit_count.message}</p>
              )}
            </div>

            {/* Inspection Count */}
            <div>
              <label
                htmlFor="inspection_count"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Number of Inspections <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="inspection_count"
                {...register('inspection_count', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                min={1}
                max={5}
              />
              {errors.inspection_count && (
                <p className="mt-1 text-sm text-red-600">{errors.inspection_count.message}</p>
              )}
            </div>
          </div>

          {/* Project Value (Optional) */}
          <div>
            <label
              htmlFor="project_value"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Project Value (USD) <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              id="project_value"
              value={projectValueDisplay}
              onChange={handleProjectValueChange}
              onBlur={handleProjectValueBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="$0.00"
              disabled={isLoading}
            />
            {errors.project_value && (
              <p className="mt-1 text-sm text-red-600">{errors.project_value.message}</p>
            )}
          </div>

          {/* Job Description (Optional) */}
          <div>
            <label
              htmlFor="job_description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Job Description <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <textarea
              id="job_description"
              {...register('job_description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Describe the electrical work to be performed..."
              disabled={isLoading}
            />
            {errors.job_description && (
              <p className="mt-1 text-sm text-red-600">{errors.job_description.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Calculating Permit Fees...' : 'Calculate Permit Fees'}
      </button>
    </form>
  );
}
